"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.extractDefectReplacementSlots = extractDefectReplacementSlots;
exports.reconcileDefectReplacements = reconcileDefectReplacements;
exports.applyDefectReplacements = applyDefectReplacements;
exports.getOrderedPositionalReplacementValues = getOrderedPositionalReplacementValues;
const POSITIONAL_KEY_PATTERN = /^slot-(\d+)$/;
const PLACEHOLDER_PATTERN = /\$\{([^}]+)\}|_{3,}/g;
function positionalKey(index) {
    return `slot-${String(index).padStart(3, '0')}`;
}
function extractDefectReplacementSlots(content) {
    if (!content)
        return [];
    const slots = [];
    const seenNamedKeys = new Set();
    let positionalIndex = 0;
    let occurrenceIndex = 0;
    for (const match of content.matchAll(PLACEHOLDER_PATTERN)) {
        const namedKey = match[1]?.trim();
        if (namedKey) {
            if (!seenNamedKeys.has(namedKey)) {
                seenNamedKeys.add(namedKey);
                slots.push({
                    key: namedKey,
                    kind: 'named',
                    token: match[0],
                    occurrenceIndex,
                });
            }
        }
        else {
            positionalIndex += 1;
            slots.push({
                key: positionalKey(positionalIndex),
                kind: 'positional',
                token: match[0],
                occurrenceIndex,
            });
        }
        occurrenceIndex += 1;
    }
    return slots;
}
function reconcileDefectReplacements(content, existing, options = {}) {
    if (content == null)
        return { ...(existing ?? {}) };
    const preservePositional = options.preservePositional ?? true;
    return Object.fromEntries(extractDefectReplacementSlots(content).map((slot) => [
        slot.key,
        slot.kind === 'positional' && !preservePositional
            ? ''
            : existing?.[slot.key] ?? '',
    ]));
}
function applyDefectReplacements(content, replacements, encodeValue = (value) => value) {
    if (!replacements)
        return content;
    let rendered = content;
    const slots = extractDefectReplacementSlots(content);
    const namedKeys = new Set(slots.filter((slot) => slot.kind === 'named').map((slot) => slot.key));
    rendered = rendered.replace(/\$\{([^}]+)\}/g, (token, rawKey) => {
        const key = rawKey.trim();
        if (!namedKeys.has(key))
            return token;
        const value = replacements[key];
        return value ? encodeValue(value) : token;
    });
    const hasStablePositionalKeys = Object.keys(replacements).some((key) => POSITIONAL_KEY_PATTERN.test(key));
    const legacyValues = hasStablePositionalKeys
        ? []
        : Object.entries(replacements)
            .filter(([key]) => !key.startsWith('_') && !namedKeys.has(key))
            .map(([, value]) => value);
    let positionalIndex = 0;
    rendered = rendered.replace(/_{3,}/g, (token) => {
        positionalIndex += 1;
        const stableValue = replacements[positionalKey(positionalIndex)];
        const value = hasStablePositionalKeys
            ? stableValue
            : legacyValues[positionalIndex - 1];
        return value ? encodeValue(value) : token;
    });
    return rendered;
}
function getOrderedPositionalReplacementValues(replacements) {
    if (!replacements)
        return [];
    const stableEntries = Object.entries(replacements)
        .map(([key, value]) => {
        const match = key.match(POSITIONAL_KEY_PATTERN);
        return match ? { index: Number(match[1]), value } : null;
    })
        .filter((entry) => entry !== null)
        .sort((left, right) => left.index - right.index);
    if (stableEntries.length > 0) {
        return stableEntries.map((entry) => entry.value);
    }
    return Object.entries(replacements)
        .filter(([key]) => !key.startsWith('_'))
        .map(([, value]) => value);
}
